import { toastItem } from "./primitives.ts";

export interface ToastControllerCapabilities {
  document: Document;
  retrySync: () => Promise<boolean>;
  afterActionUndo: () => Promise<void>;
  afterLocalUndo: () => void;
  schedule?: (callback: () => void, delay: number) => void;
}

export interface ToastController {
  notify: (message: string, error?: boolean) => void;
  retrySync: () => void;
  actionUndo: (message: string, undo: () => void | Promise<void>) => void;
  localUndo: (message: string, undo: () => void | Promise<void>) => void;
}

export function createToastController(capabilities: ToastControllerCapabilities): ToastController {
  const schedule = capabilities.schedule || ((callback, delay) => { setTimeout(callback, delay); });

  function region(error = false): HTMLElement | null {
    const openDialogs = capabilities.document.querySelectorAll<HTMLDialogElement>("dialog[open]");
    const dialog = openDialogs.item(openDialogs.length - 1);
    let target: HTMLElement | null = dialog?.querySelector(".dialog-toast-region") || null;
    if (dialog && !target) {
      target = capabilities.document.createElement("div");
      target.className = "dialog-toast-region";
      target.setAttribute("aria-live", error ? "assertive" : "polite");
      dialog.append(target);
    }
    return target || capabilities.document.querySelector<HTMLElement>("#toast-region");
  }

  function expire(item: HTMLElement, delay: number): void {
    schedule(() => {
      if (!item.isConnected) return;
      item.classList.add("is-leaving");
      schedule(() => item.remove(), 180);
    }, delay);
  }

  function notify(message: string, error = false): void {
    const target = region(error);
    if (!target) return;
    const item = toastItem(message, error, capabilities.document);
    target.append(item);
    expire(item, 2600);
  }

  function actionButton(item: HTMLElement, label: string): HTMLButtonElement {
    const action = capabilities.document.createElement("button");
    action.type = "button";
    action.textContent = label;
    item.append(action);
    return action;
  }

  // Opt-in surfaces preserve this region when repainting and move its actions
  // back to the page on close. Other dialogs retain their existing behavior.
  function actionRegion(): HTMLElement | null {
    const dialogs = capabilities.document.querySelectorAll<HTMLDialogElement>("dialog[open]");
    return dialogs.item(dialogs.length - 1)?.hasAttribute("data-action-toasts")
      ? region()
      : capabilities.document.querySelector<HTMLElement>("#toast-region");
  }

  function retrySync(): void {
    const target = capabilities.document.querySelector<HTMLElement>("#toast-region");
    if (!target || target.querySelector("[data-sync-retry]")) return;
    const item = toastItem("Some changes could not synchronize", true, capabilities.document);
    item.classList.add("toast--action");
    item.dataset.syncRetry = "true";
    const action = actionButton(item, "Retry");
    action.addEventListener("click", async () => {
      action.disabled = true;
      if (await capabilities.retrySync()) item.remove();
      else action.disabled = false;
    });
    target.append(item);
  }

  function actionUndo(message: string, undo: () => void | Promise<void>): void {
    const target = actionRegion();
    if (!target) return;
    const item = toastItem(message, false, capabilities.document);
    item.classList.add("toast--action");
    const action = actionButton(item, "Undo");
    action.dataset.taskUndo = "true";
    action.title = "Undo (Ctrl/⌘ Z)";
    action.addEventListener("click", async () => {
      action.disabled = true;
      try {
        await undo();
        item.remove();
        await capabilities.afterActionUndo();
        notify("Action undone");
      } catch {
        action.disabled = false;
        notify("Could not undo the action", true);
      }
    });
    target.append(item);
    expire(item, 6000);
  }

  function localUndo(message: string, undo: () => void | Promise<void>): void {
    const target = actionRegion();
    if (!target) return;
    const item = toastItem(message, false, capabilities.document);
    item.classList.add("toast--action");
    const action = actionButton(item, "Undo");
    action.dataset.taskUndo = "true";
    action.title = "Undo (Ctrl/⌘ Z)";
    action.addEventListener("click", async () => {
      action.disabled = true;
      try {
        await undo();
        item.remove();
        capabilities.afterLocalUndo();
        notify("Action undone");
      } catch {
        action.disabled = false;
        notify("Could not undo the action", true);
      }
    });
    target.append(item);
    expire(item, 6000);
  }

  return { notify, retrySync, actionUndo, localUndo };
}
